import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { deleteMember, fetchMembers, importMembersFromSpreadsheet, updateMemberStatus } from "@/features/admin/api";
import { useAdminSession } from "@/features/admin/useAdminSession";
import type { AdminMember, AdminMemberImportReport } from "@/features/admin/types";
import { exportRowsToExcel } from "@/shared/export/excel";

import PaginationControls from "./PaginationControls";

type FilterStatus = "tous" | "en_attente" | "actif" | "suspendu" | "refuse";
const MEMBERS_PER_PAGE = 20;
const IMPORT_TOAST_STORAGE_KEY = "asc-admin-members-import-toast";
const TOAST_DURATION_MS = 10000;

interface MembresTabProps {
  onMembersChanged?: () => void | Promise<void>;
}

function summarizeSkippedReasons(report: AdminMemberImportReport) {
  if (report.skipped.length === 0) {
    return "";
  }

  const reasonCounts = report.skipped.reduce((counts, skippedRow) => {
    counts.set(skippedRow.reason, (counts.get(skippedRow.reason) ?? 0) + 1);
    return counts;
  }, new Map<string, number>());

  return Array.from(reasonCounts.entries())
    .map(([reason, count]) => `${reason} (${count} ligne${count > 1 ? "s" : ""})`)
    .join("; ");
}

function buildImportToastMessage(report: AdminMemberImportReport) {
  const notImportedCount = Math.max(0, report.totalRows - report.createdCount);
  const importedLabel = `${report.createdCount} personne${report.createdCount > 1 ? "s" : ""} importée${report.createdCount > 1 ? "s" : ""}`;

  if (notImportedCount === 0) {
    return `Import terminé : ${importedLabel} sur ${report.totalRows}.`;
  }

  const reasonSummary = summarizeSkippedReasons(report);
  const notImportedLabel = `${notImportedCount} personne${notImportedCount > 1 ? "s" : ""} non importée${notImportedCount > 1 ? "s" : ""}`;
  const reasonPart = reasonSummary ? ` Raisons : ${reasonSummary}.` : "";

  return `Import terminé : ${importedLabel} sur ${report.totalRows}. ${notImportedLabel}.${reasonPart}`;
}

export default function MembresTab({ onMembersChanged }: MembresTabProps) {
  const { request } = useAdminSession();
  const [members, setMembers] = useState<AdminMember[]>([]);
  const [filter, setFilter] = useState<FilterStatus>("tous");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<AdminMember | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(() => {
    if (typeof window === "undefined") {
      return null;
    }

    const rawValue = sessionStorage.getItem(IMPORT_TOAST_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    try {
      const parsed = JSON.parse(rawValue) as { msg: string; type: "success" | "error"; expiresAt: number };
      if (parsed.expiresAt <= Date.now()) {
        sessionStorage.removeItem(IMPORT_TOAST_STORAGE_KEY);
        return null;
      }

      return { msg: parsed.msg, type: parsed.type };
    } catch {
      sessionStorage.removeItem(IMPORT_TOAST_STORAGE_KEY);
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [deletingMemberId, setDeletingMemberId] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState<AdminMemberImportReport | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const toastTimerRef = useRef<number | null>(null);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const nextMembers = await fetchMembers(request);
      setMembers(nextMembers);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger les membres";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  const showToast = (msg: string, type: "success" | "error") => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
    const expiresAt = Date.now() + TOAST_DURATION_MS;
    setToast({ msg, type });
    if (typeof window !== "undefined") {
      sessionStorage.setItem(
        IMPORT_TOAST_STORAGE_KEY,
        JSON.stringify({
          msg,
          type,
          expiresAt
        })
      );
    }
    toastTimerRef.current = window.setTimeout(() => {
      setToast(null);
      if (typeof window !== "undefined") {
        sessionStorage.removeItem(IMPORT_TOAST_STORAGE_KEY);
      }
      toastTimerRef.current = null;
    }, TOAST_DURATION_MS);
  };

  useEffect(() => {
    const rawValue = typeof window !== "undefined" ? sessionStorage.getItem(IMPORT_TOAST_STORAGE_KEY) : null;

    if (rawValue && !toastTimerRef.current) {
      try {
        const parsed = JSON.parse(rawValue) as { expiresAt: number };
        const remainingTime = parsed.expiresAt - Date.now();

        if (remainingTime > 0) {
          toastTimerRef.current = window.setTimeout(() => {
            setToast(null);
            sessionStorage.removeItem(IMPORT_TOAST_STORAGE_KEY);
            toastTimerRef.current = null;
          }, remainingTime);
        } else {
          sessionStorage.removeItem(IMPORT_TOAST_STORAGE_KEY);
          setToast(null);
        }
      } catch {
        sessionStorage.removeItem(IMPORT_TOAST_STORAGE_KEY);
      }
    }

    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    };
  }, []);

  const handleStatusChange = async (id: string, status: AdminMember["statut"], successMessage: string, type: "success" | "error") => {
    try {
      await updateMemberStatus(request, id, status);
      setSelected(null);
      showToast(successMessage, type);
      await loadMembers();
      await onMembersChanged?.();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Mise à jour impossible";
      setError(message);
      showToast(message, "error");
    }
  };

  const handleDeleteMember = async (member: AdminMember) => {
    setDeletingMemberId(member.id);

    try {
      await deleteMember(request, member.id);
      setSelected(null);
      showToast(`Le compte de ${member.prenom} ${member.nom} a été supprimé.`, "success");
      await loadMembers();
      await onMembersChanged?.();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Suppression impossible";
      setError(message);
      showToast(message, "error");
    } finally {
      setDeletingMemberId(null);
    }
  };

  const handleImportFile = async (file: File | null) => {
    if (!file) {
      return;
    }

    setImporting(true);
    setError(null);

    try {
      const report = await importMembersFromSpreadsheet(request, file);
      setImportReport(report);

      if (report.createdCount > 0) {
        showToast(buildImportToastMessage(report), "success");
      } else {
        showToast(`Import terminé : aucune personne importée sur ${report.totalRows}. ${summarizeSkippedReasons(report) ? `Raisons : ${summarizeSkippedReasons(report)}.` : "Aucune ligne valide n'a été trouvée."}`, "error");
      }

      await loadMembers();
      await onMembersChanged?.();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Import échoué.";
      setError(message);
      showToast(message === "Import échoué." ? message : `Import échoué. ${message}`, "error");
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const filtered = useMemo(() => {
    return members.filter((member) => {
      const matchFilter = filter === "tous" || member.statut === filter;
      const matchSearch =
        search === "" ||
        `${member.prenom} ${member.nom}`.toLowerCase().includes(search.toLowerCase()) ||
        (member.email ?? "").toLowerCase().includes(search.toLowerCase()) ||
        (member.matricule ?? "").toLowerCase().includes(search.toLowerCase());

      return matchFilter && matchSearch;
    });
  }, [filter, members, search]);

  const counts = useMemo(() => ({
    tous: members.length,
    en_attente: members.filter((member) => member.statut === "en_attente").length,
    actif: members.filter((member) => member.statut === "actif").length,
    suspendu: members.filter((member) => member.statut === "suspendu").length,
    refuse: members.filter((member) => member.statut === "refuse").length
  }), [members]);

  const statusConfig: Record<AdminMember["statut"], { label: string; cls: string }> = {
    en_attente: { label: "En attente", cls: "bg-amber-100 text-amber-600" },
    actif: { label: "Actif", cls: "bg-emerald-100 text-emerald-600" },
    suspendu: { label: "Suspendu", cls: "bg-red-100 text-red-500" },
    refuse: { label: "Refusé", cls: "bg-gray-100 text-gray-500" }
  };

  const cotisationConfig: Record<AdminMember["cotisation"], { label: string; cls: string }> = {
    payee: { label: "Payée", cls: "bg-emerald-100 text-emerald-600" },
    en_attente: { label: "En attente", cls: "bg-amber-100 text-amber-600" },
    impayee: { label: "Impayée", cls: "bg-red-100 text-red-500" }
  };

  const handleExportMembers = async () => {
    if (filtered.length === 0) {
      showToast("Aucun membre à exporter avec le filtre actuel.", "error");
      return;
    }

    await exportRowsToExcel(
      "membres-asc-sonatel",
      "Membres",
      filtered.map((member) => ({
        Prénom: member.prenom,
        Nom: member.nom,
        Email: member.email ?? "",
        Matricule: member.matricule ?? "",
        Téléphone: member.telephone ?? "",
        Direction: member.departement ?? "",
        Sports: member.sport.join(", "),
        Statut: statusConfig[member.statut].label,
        Cotisation: cotisationConfig[member.cotisation].label,
        "Date inscription": member.dateInscription,
        "Date validation": member.dateValidation ?? ""
      }))
    );
  };

  const totalPages = Math.max(1, Math.ceil(filtered.length / MEMBERS_PER_PAGE));
  const paginatedMembers = useMemo(() => {
    const startIndex = (currentPage - 1) * MEMBERS_PER_PAGE;
    return filtered.slice(startIndex, startIndex + MEMBERS_PER_PAGE);
  }, [currentPage, filtered]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, search]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  return (
    <div className="space-y-6 relative">
      {toast && (
        <div className={`fixed top-6 right-6 z-50 flex max-w-[min(640px,calc(100vw-2rem))] items-start gap-2 rounded-xl px-5 py-3 text-sm font-medium leading-snug text-white shadow-lg ${toast.type === "success" ? "bg-emerald-500" : "bg-red-500"}`}>
          <i className={`${toast.type === "success" ? "ri-checkbox-circle-line" : "ri-close-circle-line"} mt-0.5 text-lg`}></i>
          <span>{toast.msg}</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#1A1A1A]">Gestion des Membres</h1>
          <p className="text-gray-500 text-sm mt-1">Validez les adhésions et gérez les statuts membres</p>
          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(event) => void handleImportFile(event.target.files?.[0] ?? null)}
          />
          <button
            onClick={() => void handleExportMembers()}
            disabled={loading || filtered.length === 0}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          >
            <i className="ri-download-2-line"></i>
            Exporter Excel
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#1A8C7A] text-white text-sm font-medium hover:bg-[#157567] disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          >
            <i className={`${importing ? "ri-loader-4-line animate-spin" : "ri-file-excel-2-line"}`}></i>
            {importing ? "Import en cours..." : "Importer Excel"}
          </button>
          <button
            onClick={() => void loadMembers()}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#E8521A] text-white text-sm font-medium hover:bg-[#d44a16] transition-colors whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            Actualiser
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-[#1A8C7A]/15 bg-[#F2FBF9] p-4">
        <h2 className="text-sm font-semibold text-[#1A1A1A]">Import Excel des membres</h2>
        <p className="mt-1 text-sm text-gray-600">
          Colonnes attendues dans le fichier: <span className="font-medium">prenom, nom, Telephone, Direction</span>. Les colonnes <span className="font-medium">Adresse mail</span> et <span className="font-medium">matricule</span> sont optionnelles. Le mot de passe par defaut applique a l&apos;import est <span className="font-medium">sonatel221</span>.
        </p>
        {importReport ? (
          <div className="mt-3 rounded-xl bg-white/80 p-4 text-sm text-gray-600">
            <p className="font-semibold text-[#1A1A1A]">
              {importReport.createdCount} membre(s) créé(s) sur {importReport.totalRows} ligne(s).
            </p>
            {importReport.skipped.length > 0 ? (
              <div className="mt-3 space-y-2">
                <p className="font-medium text-[#E8521A]">Lignes ignorées</p>
                {importReport.skipped.slice(0, 5).map((item) => (
                  <div key={`${item.row}-${item.email ?? item.matricule ?? item.reason}`} className="rounded-lg border border-orange-100 bg-orange-50 px-3 py-2 text-xs text-orange-700">
                    Ligne {item.row} : {item.reason}
                    {item.email ? ` · ${item.email}` : ""}
                    {item.matricule ? ` · ${item.matricule}` : ""}
                    {item.telephone ? ` · ${item.telephone}` : ""}
                  </div>
                ))}
                {importReport.skipped.length > 5 ? (
                  <p className="text-xs text-gray-500">+ {importReport.skipped.length - 5} autre(s) ligne(s) ignorée(s)</p>
                ) : null}
              </div>
            ) : (
              <p className="mt-2 text-emerald-700">Toutes les lignes valides du fichier ont bien été importées.</p>
            )}
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        {(Object.keys(counts) as FilterStatus[]).map((value) => (
          <button
            key={value}
            onClick={() => setFilter(value)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
              filter === value
                ? "bg-[#E8521A] text-white"
                : "bg-white text-gray-600 border border-gray-200 hover:border-[#E8521A] hover:text-[#E8521A]"
            }`}
          >
            {value === "tous" ? "Tous" : value === "en_attente" ? "En attente" : value === "actif" ? "Actifs" : value === "suspendu" ? "Suspendus" : "Refusés"}
            <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${filter === value ? "bg-white/20" : "bg-gray-100"}`}>
              {counts[value]}
            </span>
          </button>
        ))}
      </div>

      <div className="relative">
        <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
        <input
          type="text"
          placeholder="Rechercher par nom, email ou matricule..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
        />
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Membre</th>
                <th className="text-left px-5 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Matricule</th>
                <th className="text-left px-5 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Direction</th>
                <th className="text-left px-5 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Sports</th>
                <th className="text-left px-5 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Cotisation</th>
                <th className="text-left px-5 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Statut</th>
                <th className="text-left px-5 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                <th className="text-right px-5 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <tr key={index}>
                    <td colSpan={8} className="px-5 py-4">
                      <div className="h-12 rounded-xl bg-gray-100 animate-pulse"></div>
                    </td>
                  </tr>
                ))
              ) : paginatedMembers.map((member) => {
                const status = statusConfig[member.statut];
                const cotisation = cotisationConfig[member.cotisation];

                return (
                  <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <img src={member.avatar ?? ""} alt={member.nom} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                        <div>
                          <div className="text-sm font-semibold text-[#1A1A1A]">{member.prenom} {member.nom}</div>
                          <div className="text-xs text-gray-500">{member.email ?? "Email non renseigné"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600 font-mono">{member.matricule ?? "—"}</td>
                    <td className="px-5 py-4 text-sm text-gray-600">{member.departement ?? "—"}</td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap gap-1">
                        {member.sport.slice(0, 2).map((sport) => (
                          <span key={sport} className="text-xs bg-orange-50 text-[#E8521A] px-2 py-0.5 rounded-full">{sport}</span>
                        ))}
                        {member.sport.length > 2 ? (
                          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">+{member.sport.length - 2}</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${cotisation.cls}`}>{cotisation.label}</span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${status.cls}`}>{status.label}</span>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-500">{member.dateInscription}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => setSelected(member)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-orange-100 hover:text-[#E8521A] transition-colors"
                        >
                          <i className="ri-eye-line text-sm"></i>
                        </button>
                        {member.statut === "en_attente" ? (
                          <>
                            <button
                              onClick={() => void handleStatusChange(member.id, "actif", "Adhésion validée avec succès", "success")}
                              className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors"
                            >
                              <i className="ri-check-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => void handleStatusChange(member.id, "refuse", "Adhésion refusée", "error")}
                              className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-100 text-red-500 hover:bg-red-200 transition-colors"
                            >
                              <i className="ri-close-line text-sm"></i>
                            </button>
                          </>
                        ) : null}
                        {member.statut === "actif" ? (
                          <button
                            onClick={() => void handleStatusChange(member.id, "suspendu", "Membre suspendu", "error")}
                            title="Suspendre le compte"
                            aria-label="Suspendre le compte"
                            className="w-8 h-8 flex items-center justify-center rounded-full bg-red-100 text-red-500 hover:bg-red-200 transition-colors"
                          >
                            <i className="ri-forbid-2-line text-sm"></i>
                          </button>
                        ) : null}
                        {member.statut === "suspendu" ? (
                          <button
                            onClick={() => void handleStatusChange(member.id, "actif", "Membre réactivé", "success")}
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors"
                          >
                            <i className="ri-play-line text-sm"></i>
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!loading && filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <i className="ri-user-search-line text-4xl mb-3 block"></i>
              <p className="text-sm">Aucun membre trouvé</p>
            </div>
          ) : null}
        </div>
      </div>

      {!loading && filtered.length > 0 ? (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemLabel="membres"
        />
      ) : null}

      {selected ? (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-[#1A1A1A] text-lg">Détails du membre</h3>
              <button onClick={() => setSelected(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-4">
                <img src={selected.avatar ?? ""} alt={selected.nom} className="w-16 h-16 rounded-full object-cover" />
                <div>
                  <div className="text-xl font-bold text-[#1A1A1A]">{selected.prenom} {selected.nom}</div>
                  <div className="text-sm text-gray-500">{selected.matricule ?? "Sans matricule"}</div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium mt-1 inline-block ${statusConfig[selected.statut].cls}`}>
                    {statusConfig[selected.statut].label}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Email", value: selected.email ?? "—", icon: "ri-mail-line" },
                  { label: "Téléphone", value: selected.telephone ?? "—", icon: "ri-phone-line" },
                  { label: "Direction", value: selected.departement ?? "—", icon: "ri-building-line" },
                  { label: "Inscription", value: selected.dateInscription, icon: "ri-calendar-line" }
                ].map((field) => (
                  <div key={field.label} className="bg-gray-50 rounded-xl p-3">
                    <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                      <i className={field.icon}></i>
                      {field.label}
                    </div>
                    <div className="text-sm font-medium text-[#1A1A1A]">{field.value}</div>
                  </div>
                ))}
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-500 mb-2">Sports pratiqués</div>
                <div className="flex flex-wrap gap-2">
                  {selected.sport.length > 0 ? selected.sport.map((sport) => (
                    <span key={sport} className="text-sm bg-orange-100 text-[#E8521A] px-3 py-1 rounded-full font-medium">{sport}</span>
                  )) : <span className="text-sm text-gray-400">Aucun sport renseigné</span>}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 space-y-3">
              {selected.statut === "en_attente" ? (
                <div className="flex gap-3">
                  <button
                    onClick={() => void handleStatusChange(selected.id, "refuse", "Adhésion refusée", "error")}
                    className="flex-1 py-3 rounded-xl border-2 border-red-200 text-red-500 text-sm font-semibold hover:bg-red-50 transition-colors"
                  >
                    Refuser
                  </button>
                  <button
                    onClick={() => void handleStatusChange(selected.id, "actif", "Adhésion validée avec succès", "success")}
                    className="flex-1 py-3 rounded-xl bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 transition-colors"
                  >
                    Valider l&apos;adhésion
                  </button>
                </div>
              ) : null}
              {selected.statut === "suspendu" ? (
                <button
                  onClick={() => void handleStatusChange(selected.id, "actif", "Membre réactivé", "success")}
                  className="w-full py-3 rounded-xl bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 transition-colors"
                >
                  Réactiver le membre
                </button>
              ) : null}
              {selected.statut === "actif" ? (
                <button
                  onClick={() => void handleStatusChange(selected.id, "suspendu", "Membre suspendu", "error")}
                  className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors"
                >
                  <i className="ri-forbid-2-line"></i>
                  Suspendre le compte
                </button>
              ) : null}
              <button
                onClick={() => void handleDeleteMember(selected)}
                disabled={deletingMemberId === selected.id}
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl border border-red-200 bg-red-50 text-red-600 text-sm font-semibold hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 transition-colors"
              >
                <i className={`${deletingMemberId === selected.id ? "ri-loader-4-line animate-spin" : "ri-delete-bin-6-line"}`}></i>
                {deletingMemberId === selected.id ? "Suppression..." : "Supprimer le compte"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
